import { createSelector } from 'reselect'
import getExperienceItemsView from 'checkout/selectors/view/itemViews/getExperienceItemsView'
import { getCheckoutNeedsTravellerForms, getIsCheckoutTravellerReadinessSatisfied, shouldShowTravellerForm } from 'checkout/selectors/travellerFormSelectors'
import { getVillaItems } from './view/villa'
import { getBookingCabinPricingData, getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import getCheckoutTravellerSchema from 'checkout/selectors/getCheckoutTravellerSchema'
import getIsCheckoutTravellerSchemaFetching from './getCheckoutTravellerSchemaFetching'
import { allFormsCompleted } from 'lib/checkout/form'
import getPayableTotal from './payment/getPayableTotal'
import { getIsCheckoutOrderPayloadReady } from './payment/orderCreationSelectors'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getIsMobileAppLikeCheckoutEnabled } from './mobileAppLikeCheckoutSelectors'
import { CheckoutPageId } from 'checkout/constants/pages'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { getCartHasOnlyMerchandiseItems } from 'checkout/selectors/view/items/getMerchandiseItems'

const getCheckoutLocale = (state: App.State) => state.geo.locale

export { shouldShowTravellerForm }

export const shouldShowArrivalDetailsForm = createSelector(
  (state: App.State) => getVillaItems(state),
  (villaItems): boolean => {
    return !!villaItems.length
  },
)

/**
 * @remarks
 * Edge case for cruise items in the checkout.
 * Due to a technical limitation we cannot correctly display deposit/payment schedule options
 * until 'cabin preferences' are selected
 *
 * Agent Hub skips the order-payload readiness visibility gate: formatted cart items are not
 * ready until a primary traveller exists, which hid Payment options until Who’s going? was
 * filled. Traveller schema readiness (#33966) still applies; submit keeps the payload guard.
 * Shop also skips the visibility gate so its delivery form can report address-specific errors.
 */
export const shouldShowPaymentForm = createSelector(
  (state: App.State) => getCheckoutNeedsTravellerForms(state),
  (state: App.State) => shouldShowTravellerForm(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => getBookingCabinPricingData(state),
  (state: App.State) => getPayableTotal(state) > 0,
  (state: App.State) => getIsCheckoutTravellerReadinessSatisfied(state),
  (state: App.State) => getIsCheckoutOrderPayloadReady(state),
  (state: App.State) => getCartHasOnlyMerchandiseItems(state),
  (checkoutNeedsTravellerForms, showForm, cruiseItems, cabinPricingData, hasPayableTotal, isTravellerFormReady, isOrderPayloadReady, isShopCheckout): boolean => {
    const isPaymentPayloadGateSatisfied = isAgentHubEnabled || isShopCheckout || isOrderPayloadReady

    if (cruiseItems.length) {
      return !!cabinPricingData.hasCabinPricing && isPaymentPayloadGateSatisfied
    }

    const shouldShowPayment = !checkoutNeedsTravellerForms || showForm || hasPayableTotal
    return shouldShowPayment && isTravellerFormReady && isPaymentPayloadGateSatisfied
  },
)

export const hasUnpurchasableItems = createSelector(
  (state: App.State) => getExperienceItemsView(state),
  (experienceItemsView) => {
    return {
      hasRequiredData: experienceItemsView.hasRequiredData,
      data: experienceItemsView.hasRequiredData && experienceItemsView.data.some(item => (
        item.ticketViews.some(ticket => ticket.unavailable)
      )),
    }
  },
)

const getRequiredNumberOfForms = createSelector(
  (state: App.State) => getCheckoutTravellerSchema(state),
  (schemaData): number => schemaData?.accommodationFlightSchema ?
    Object.keys(schemaData.accommodationFlightSchema.properties).length :
    0,
)

export const getCheckoutSingleStep = createSelector(
  (state: App.State) => state.checkout.isCartRestored,
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getIsCheckoutTravellerSchemaFetching(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => shouldShowTravellerForm(state),
  (state: App.State) => hasUnpurchasableItems(state),
  (state: App.State) => getRequiredNumberOfForms(state),
  getCheckoutLocale,
  (isCartRestored, schemaData, isFetchingSchema, travellerForms,
    isGift, shouldShowForm, hasUnpurchasableItems, requiredTravellerCount, _locale): App.Checkout.Step => {
    const intl = getCurrentIntl()
    const isFormReady = (isCartRestored && !isFetchingSchema && (schemaData || !shouldShowForm))
    const reviewGiftLabel = intl.formatMessage({
      id: 'TzVpZyqIlZpqwtOJojE2',
      defaultMessage: 'Review your gift',
      description: 'Single checkout step label when reviewing a gift purchase in checkout',
    })
    const reviewAndPayLabel = intl.formatMessage({
      id: 'gQEaHDKGcuGHj8cSea4N',
      defaultMessage: 'Review & Pay',
      description: 'Single checkout step label when reviewing and paying in checkout',
    })

    const result = !!schemaData?.accommodationFlightSchema &&
      travellerForms.length === requiredTravellerCount &&
      allFormsCompleted(schemaData.accommodationFlightSchema, travellerForms) &&
      !hasUnpurchasableItems.data

    return {
      id: CheckoutPageId.Purchase,
      name: isGift ? reviewGiftLabel : reviewAndPayLabel,
      shortName: CheckoutPageId.Purchase,
      isComplete: {
        hasRequiredData: isFormReady && hasUnpurchasableItems.hasRequiredData,
        data: result,
      },
    }
  },
)

// TODO: https://aussiecommerce.atlassian.net/browse/CRO-3597 Remove steps concept for new checkout as it's single step
export const getCheckoutSteps = createSelector(
  (state: App.State) => getCheckoutSingleStep(state),
  (state: App.State) => getIsMobileAppLikeCheckoutEnabled(state),
  (
    checkoutSingleStep,
    isMobileAppLikeCheckoutEnabled,
  ): Array<App.Checkout.Step> => {
    if (isMobileAppLikeCheckoutEnabled) {
      const intl = getCurrentIntl()
      const secureCheckoutLabel = intl.formatMessage({
        id: 'a6KLniHQOVFk1zB_aE8X',
        defaultMessage: 'Secure checkout',
        description: 'Checkout step label for reviewing the booked escape in the mobile app-like checkout experiment',
      })

      return [
        {
          id: CheckoutPageId.Review,
          name: secureCheckoutLabel,
          shortName: CheckoutPageId.Review,
          isComplete: {
            hasRequiredData: true,
            data: true,
          },
        },
        {
          ...checkoutSingleStep,
          id: CheckoutPageId.Payment,
          name: secureCheckoutLabel,
          shortName: CheckoutPageId.Payment,
        },
      ]
    }

    return [checkoutSingleStep]
  },
)
